import { FC, useEffect, useMemo } from "react";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { Select } from "@hostingbv/lib/src/components/form/Select";
import { Loading } from "@hostingbv/lib/src/components/Loading";
import { Role } from "@hostingbv/lib/src/api/partner/models/Role";
import { config } from "../config";

/**
 * Dropdown with the tenant's roles. While `value` is empty it selects `preselect` when that role exists, and the
 * tenant's default role otherwise.
 */
export const RoleSelect: FC<{ value: string, onChange: (roleUuid: string) => void, preselect?: string | null }> = ({ value, onChange, preselect }) => {
  const { partnerClient } = useApiClient(config)
  const { resource, loading, reload } = useFetchedResource(() => partnerClient.getRoles())
  useEffect(() => {
    reload()
  }, [reload])

  const roles: Role[] = useMemo(() => (resource?.success === true ? resource.data : []), [resource])

  useEffect(() => {
    if (value !== '' || roles.length === 0) {
      return
    }
    const role = roles.find(r => r.id === preselect) ?? roles.find(r => r.isDefault) ?? roles[0]
    onChange(role.id)
  }, [roles, value, preselect, onChange])

  const options = useMemo(() => roles.reduce((acc, role) => {
    acc[role.id] = role.isDefault ? `${role.displayName} (default)` : role.displayName
    return acc
  }, {} as { [key: string]: string }), [roles])

  return <Loading loading={loading && resource === null}>
    {resource?.success === false
      ? <div className={"font-medium text-red-600"}>{resource.error}</div>
      : <Select label={"Role"} options={options} value={value} onChange={onChange} />}
  </Loading>
}
